import { Link } from "react-router";
import { getEntry, listCollection } from "../lib/content.server.js";
import { articleJsonLd, breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import { related } from "../lib/related.js";
import { parseVideo } from "../lib/video.js";
import { surfaceFor } from "../lib/surface.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import Prose from "../components/Prose/Prose.jsx";
import VideoEmbed from "../components/VideoEmbed/VideoEmbed.jsx";
import EntryCard from "../components/EntryCard/EntryCard.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { stateName } from "../components/StateMap/StateMap.jsx";
import { track } from "../lib/analytics.js";
import styles from "./case-study.module.css";

const PRODUCT = {
  rsa3d: { label: "Road Safety Audit 3D", href: "/road-safety-audit-3d" },
  "123bim": { label: "3D Visualization", href: "/3d-visualization" },
  both: { label: "RSA 3D and 3D Visualization", href: "/road-safety-audit-3d" },
};

// The closing banner, in the product page's own words.
const CTA = {
  rsa3d: {
    image: "/images/site/rsa3d/sight-distance",
    title: "Audit your next project in 3D",
    text: "Send us a design or a scan and get a sight-distance report back, with no software to install.",
  },
  "123bim": {
    image: "/images/site/123bim/photo-brunswick",
    title: "See your project in Unreal Engine",
    text: "Send a DGN, DWG, OpenRoads or Civil 3D file and get a link to your own model.",
  },
};
CTA.both = CTA.rsa3d;

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["article"];
const surface = surfaceFor(LIGHT_SECTIONS);

export async function loader({ params }) {
  const entry = getEntry("case-studies", params.slug);
  if (!entry) throw new Response("Not Found", { status: 404 });
  const more = related(listCollection("case-studies"), entry, "product", 3);
  return { entry, more };
}

export function meta({ loaderData }) {
  if (!loaderData)
    return buildMeta({
      title: "Case study not found",
      description: "This case study does not exist.",
      path: "/case-studies",
      noindex: true,
    });
  const { entry } = loaderData;
  const path = `/case-studies/${entry.slug}`;
  return buildMeta({
    title: entry.title,
    description: entry.description,
    path,
    image: entry.image,
    type: "article",
    jsonLd: [
      articleJsonLd(entry, path),
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Case Studies", path: "/case-studies" },
        { name: entry.title, path },
      ]),
    ],
  });
}

function DownloadPdf({ href, className }) {
  return (
    <a
      className={className}
      href={href}
      download
      onClick={() => track("pdf_download", { file: href })}
    >
      Download the case study (PDF)
    </a>
  );
}

export default function CaseStudy({ loaderData }) {
  const { entry, more } = loaderData;
  const product = PRODUCT[entry.product];
  const cta = CTA[entry.product];
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Case Studies", path: "/case-studies" },
    { name: entry.title, path: `/case-studies/${entry.slug}` },
  ];
  // Most case studies already play their video inside the text; only show it again if not.
  const video = parseVideo(entry.video);
  const videoInText = video && entry.html.includes(`data-id="${video.id}"`);
  return (
    <>
      <section className={styles.hero} aria-labelledby="case-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={crumbs} />
            <h1 id="case-title" className={styles.heroTitle}>
              {entry.title}
            </h1>
            <p className={styles.heroLead}>{entry.summary}</p>
            <div className={styles.heroActions}>
              {entry.pdf ? <DownloadPdf href={entry.pdf} className="btn btn-primary" /> : null}
              <Link to={product.href} className={styles.heroLink}>
                {product.label}
              </Link>
            </div>
          </div>
          {entry.image ? (
            <Picture
              src={entry.image}
              alt={entry.imageAlt}
              ratio="16 / 9"
              widths={[480, 960, 1600]}
              fallbackWidth={960}
              sizes="(min-width: 64rem) 50vw, 100vw"
              priority
              className={styles.heroImage}
            />
          ) : null}
        </div>
      </section>

      <section className="section" aria-label="The case at a glance">
        <div className={`container ${styles.glance} ${entry.highlight ? "" : styles.factsOnly}`}>
          {entry.highlight ? (
            <p className={styles.result}>
              <strong>{entry.highlight}</strong>
              <span>{entry.highlightText}</span>
            </p>
          ) : null}
          <dl className={styles.facts}>
            <div>
              <dt>Client</dt>
              <dd>{entry.client}</dd>
            </div>
            {entry.state ? (
              <div>
                <dt>Location</dt>
                <dd>{stateName(entry.state)}</dd>
              </div>
            ) : null}
            <div>
              <dt>Product</dt>
              <dd>{product.label}</dd>
            </div>
            <div>
              <dt>Published</dt>
              <dd>{entry.date.slice(0, 4)}</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* The text, one part per heading (rehypeParts), each heading beside its text. */}
      <section className="section" data-surface={surface("article")} aria-label="The case study">
        <div className="container">
          <Prose html={entry.html} className={styles.article} />
          {video && !videoInText ? (
            <div className={`prose ${styles.article}`}>
              <section className="part">
                <h2>Video</h2>
                <div className="part-body">
                  <VideoEmbed video={entry.video} title={entry.title} poster={entry.image} />
                </div>
              </section>
            </div>
          ) : null}
          {entry.pdf ? (
            <p className={styles.download}>
              <DownloadPdf href={entry.pdf} className="btn btn-secondary" />
            </p>
          ) : null}
        </div>
      </section>

      {more.length ? (
        <section className="section" aria-labelledby="more-title">
          <div className="container">
            <SectionHeader
              id="more-title"
              title="More case studies"
              link={{ to: "/case-studies", label: "All case studies" }}
            />
            <div className="grid grid-3">
              {more.map((cs) => (
                <EntryCard
                  key={cs.slug}
                  kind="case-study"
                  href={`/case-studies/${cs.slug}`}
                  image={cs.image}
                  imageAlt={cs.imageAlt}
                  title={cs.title}
                  description={cs.summary}
                  meta={cs.client}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <CTABand
        variant="statement"
        image={cta.image}
        title={cta.title}
        text={cta.text}
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
